import { thunk, Action, Thunk, action, Computed, computed } from 'easy-peasy'
import { CLPool, CLPoolKey, Token } from '../../atoms'
import { Network } from '../../services/token.service'
import { ALL_FEES, SPACING_PER_FEE, clPoolsArrayToRecord, fetchPoolPrice, fetchPoolsFromFactory, getCLPool, poolKeyToString } from './helper'
import { getTokensOfOwner } from '../../services/nft_pool'
import { PositionValue, getLegacyPositions_phase0 } from '../../services/position'
import { loadPoolsData } from '../../components/charts/chartsApi'
import { getToken } from '../tokens/helper'

export interface CLPosition {
    fee: number
    feeGrowthInside0LastX128: string
    feeGrowthInside1LastX128: string
    liquidity: string
    positionIndex: number
    tickLower: number
    tickUpper: number
    token0: string
    token1: string
    tokensOwed0: string
    tokensOwed1: string
    fees: PositionValue | undefined
    poolAddress: string
    poolCurrentTick: number
    legacyPoolId?: number
    legacyLpTokenAddress?: string
    legacyLpTokenAmount?: string
    value: {
        token1Address: string
        token1Amount: string
        token1AmountWei: string
        token2Address: string
        token2Amount: string
        token2AmountWei: string
    }
}

export interface CLPoolsModel {
    pools: Record<string, Record<string, CLPool>> // network=>{key=>pool}
    positions: Record<string, CLPosition[]>
    legacyPositions: Record<string, CLPosition[]>
    // find all pools for a token pair and possibly fee
    discoverPools: Thunk<CLPoolsModel, { network: Network; token1: Token; token2: Token; fees?: number[] }>
    loadPools: Thunk<CLPoolsModel, { network: Network }>
    discoverPool: Thunk<CLPoolsModel, { network: Network; token1: Token; token2: Token; fee: number }>
    loadPositons: Thunk<CLPoolsModel, { network: Network; address: string; selectedPosition?: number }>
    loadLegacyPositons: Thunk<CLPoolsModel, { network: Network }>
    setPools: Action<CLPoolsModel, { network: Network; pools: Record<string, CLPool> }>
    setPostions: Action<CLPoolsModel, { network: Network; positions: CLPosition[] }>
    setLegacyPostions: Action<CLPoolsModel, { network: Network; positions: CLPosition[] }>
    poolBalance: Record<string, Record<number, string>>
    // eslint-disable-next-line no-unused-vars
    getPoolByAddress: Computed<CLPoolsModel, (network: string, address: string) => CLPool | undefined>
    // eslint-disable-next-line no-unused-vars
    getClPool: Computed<CLPoolsModel, (network: string, token1: string, token2: string, fee: number) => CLPool | undefined>
    // eslint-disable-next-line no-unused-vars
    getCLPositions: Computed<CLPoolsModel, (network: string) => CLPosition[] | []>
    // eslint-disable-next-line no-unused-vars
    getCLLegacyPositions: Computed<CLPoolsModel, (network: string) => CLPosition[] | []>
    // eslint-disable-next-line no-unused-vars
    getCLPosition: Computed<CLPoolsModel, (network: string, index: number) => CLPosition | undefined>
    getCLPositionByIndex: Computed<CLPoolsModel, (network: string, index: number) => CLPosition | undefined>

    loadPoolsBalance: Thunk<
        CLPoolsModel,
        {
            network: Network
            walletAddress: string
            amountOfPools: number
            poolId?: number
        }
    >

    setPoolBalance: Action<CLPoolsModel, { network: string; balance: Record<number, string> }>
    updatePoolPrice: Thunk<CLPoolsModel, { network: Network; pool: CLPool }>
}

const clPoolsModel: CLPoolsModel = {
    pools: {},
    poolBalance: {},
    positions: {},
    legacyPositions: {},

    getPoolByAddress: computed((state) => (network, address) => {
        for (let key in state.pools[network]) {
            if (state.pools[network][key].address === address) return state.pools[network][key]
        }
    }),

    getClPool: computed((state) => (network, token1, token2, fee) => {
        // const token1Pool = Object.values(state.pools[network])
        for (let key in state.pools[network]) {
            if (state.pools[network][key].token1.address === token1 && state.pools[network][key].token2.address === token2 && state.pools[network][key].fee === fee)
                return state.pools[network][key]
            if (state.pools[network][key].token1.address === token2 && state.pools[network][key].token2.address === token1 && state.pools[network][key].fee === fee)
                return state.pools[network][key]
            // return 'Not exist'
        }
    }),

    getCLPositions: computed((state) => (network) => {
        return state.positions[network] || []
    }),

    getCLLegacyPositions: computed((state) => (network) => {
        return state.legacyPositions[network] || []
    }),

    getCLPosition: computed((state) => (network, index) => {
        return state.positions[network][index] || undefined
    }),

    getCLPositionByIndex: computed((state) => (network, index) => {
        const positions = state.positions[network]
        if (positions && positions.length > 0) return positions.find((item) => item && item.positionIndex === index)
        return undefined
        // return state.positions[network][index] || undefined
    }),

    setPools: action((state, payload) => {
        if (!state.pools[payload.network]) state.pools = { ...state.pools, [payload.network]: {} }
        state.pools[payload.network] = {
            ...state.pools[payload.network],
            ...payload.pools
        }
    }),

    setPostions: action((state, payload) => {
        state.positions[payload.network] = payload.positions
    }),

    setLegacyPostions: action((state, payload) => {
        state.legacyPositions[payload.network] = payload.positions
    }),

    setPoolBalance: action((state, payload) => {
        if (!state.poolBalance[payload.network]) state.poolBalance = { ...state.poolBalance, [payload.network]: {} }
        state.poolBalance[payload.network] = {
            ...state.poolBalance[payload.network],
            ...payload.balance
        }
    }),

    updatePoolPrice: thunk(async (actions, payload) => {
        if (!payload.network) return
        const pool = await fetchPoolPrice(payload.network, payload.pool)
        // pool is the updated pool
        const poolKey: CLPoolKey = {
            token1: pool.token1.address,
            token2: pool.token2.address,
            fee: pool.fee
        }

        const pools: Record<string, CLPool> = {}
        pools[poolKeyToString(poolKey)] = pool

        actions.setPools({
            network: payload.network,
            pools: pools
        })
    }),

    discoverPools: thunk(async (actions, payload) => {
        // console.log('YSYS discover pools payload=', payload)
        if (!payload.network) return
        const feeList = payload.fees ? payload.fees : ALL_FEES
        // now we run on all possible fees, and check if we already have a pool
        // for that fee.
        // If not, we try to fetch it from factory contract
        const poolsToDiscover: Array<CLPoolKey> = []
        const notExistFeePool: number[] = []
        feeList.forEach((fee) => {
            const existingPool = getCLPool(payload.network, payload.token1, payload.token2, fee)
            if (existingPool === undefined) {
                poolsToDiscover.push({
                    token1: payload.token1.address,
                    token2: payload.token2.address,
                    fee: fee
                })
            } else {
                notExistFeePool.push(fee)
            }
        })
        // console.log('YSYS poolsToDisc=', poolsToDiscover)
        // now fetch all these
        const pools = await fetchPoolsFromFactory(payload.network, poolsToDiscover)
        if (pools.length > 0) {
            actions.setPools({
                network: payload.network,
                pools: clPoolsArrayToRecord(pools)
            })
        } else {
            const pools: CLPool[] = []
            notExistFeePool.forEach((feeLevel) => {
                pools.push({ address: '', fee: feeLevel, token1: payload.token1, token2: payload.token2, tickSpacing: 0, currentPrice: '0', currentTick: 0 })
            })
            actions.setPools({
                network: payload.network,
                pools: clPoolsArrayToRecord(pools)
            })
        }
    }),

    loadPools: thunk(async (actions, payload) => {
        const pools = await loadPoolsData(payload.network)
        const CLPools = [] as CLPool[]
        pools.map((pool) => {
            const token0 = getToken(payload.network, pool.token0.address)
            const token1 = getToken(payload.network, pool.token1.address)
            if (!token0 || !token1) return
            CLPools.push({ address: pool.poolkey, fee: pool.pool_fee * 10000, tickSpacing: SPACING_PER_FEE[pool.pool_fee * 10000], token1: token0!, token2: token1! })
        })

        actions.setPools({
            network: payload.network,
            pools: clPoolsArrayToRecord(CLPools)
        })
    }),

    discoverPool: thunk(async (actions, payload) => {
        console.log('discoverPool ')
        // console.log('YSYS discover pool payload=', payload)
        if (!payload.network) return
        // const feeList = payload.fees ? payload.fees : ALL_FEES
        // now we run on all possible fees, and check if we already have a pool
        // for that fee.
        // If not, we try to fetch it from factory contract
        const poolsToDiscover: Array<CLPoolKey> = []
        // feeList.forEach((fee) => {
        const existingPool = getCLPool(payload.network, payload.token1, payload.token2, payload.fee)

        if (existingPool === undefined) {
            poolsToDiscover.push({
                token1: payload.token1.address,
                token2: payload.token2.address,
                fee: payload.fee
            })
        }
        const emptyPool = { address: '', fee: payload.fee, token1: payload.token1, token2: payload.token2, tickSpacing: 0, currentPrice: '0', currentTick: 0 }
        if (existingPool) return existingPool // add here load pool if no address?
        console.log('poolsToDiscover ', poolsToDiscover)
        const pools = await fetchPoolsFromFactory(payload.network, poolsToDiscover)
        if (pools.length > 0) {
            actions.setPools({
                network: payload.network,
                pools: clPoolsArrayToRecord(pools)
            })
        } else {
            actions.setPools({
                network: payload.network,
                pools: clPoolsArrayToRecord([emptyPool])
            })
        }
        console.log('pools ', pools)
        if (pools && pools.length > 0) return pools[0]
        else return emptyPool
    }),

    loadPoolsBalance: thunk(async (actions, payload) => {
        if (!payload.network) return
        // const response = await loadPoolLPBalances(payload.network, payload.walletAddress, payload.amountOfPools, payload.poolId)
        // const balances: Record<number, string> = {}
        // response.map((balance) => {
        //     balances[balance.poolId] = balance.balance
        // })
        // actions.setPoolBalance({ network: payload.network, balance: balances })
    }),

    loadPositons: thunk(async (actions, payload, helpers) => {
        const response = await getTokensOfOwner(payload.network, payload.address, payload.selectedPosition)

        const sorted = response.sort((a, b) => {
            if (a.positionIndex > b.positionIndex) return -1
            if (b.positionIndex > a.positionIndex) return 1
            return 0
        })

        if (payload.selectedPosition === -1) actions.setPostions({ network: payload.network, positions: sorted })
        else {
            const positions = [...helpers.getState().getCLPositions(payload.network)]
            const position = response[0]
            const index = positions.findIndex((posit) => posit.positionIndex === payload.selectedPosition)
            if (index !== -1) positions[index!] = position
            else {
                positions.push(position)
            }
            actions.setPostions({ network: payload.network, positions: positions })
        }
    }),

    loadLegacyPositons: thunk(async (actions, payload) => {
        // This we will use after migration of positions to CL migrator ("phase1")
        // const response = await getLegacyPositions(payload.network)

        // currently we show the legacy myswap positions directly here
        // by reading them from old myswap
        const response = await getLegacyPositions_phase0(payload.network)
        actions.setLegacyPostions({ network: payload.network, positions: response })
    })
}

export default clPoolsModel
